import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import type { UserPublic, Role } from '@imza/shared';
import { roleAtLeast } from '@imza/shared';

interface MeResponse {
  user: UserPublic;
}

export function useAuth() {
  const queryClient = useQueryClient();

  const meQuery = useQuery<UserPublic | null>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const data = await api.get<MeResponse>('/api/auth/me');
        return data.user;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
    },
  });

  return {
    user: meQuery.data ?? null,
    isLoading: meQuery.isLoading,
    isAuthenticated: !!meQuery.data,
    logout: () => logoutMutation.mutate(),
    hasRole: (required: Role) =>
      meQuery.data ? roleAtLeast(meQuery.data.role, required) : false,
  };
}

export interface LoginInput {
  email: string;
  password: string;
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const data = await api.post<MeResponse>('/api/auth/login', input);
      return data.user;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(['auth', 'me'], user);
    },
  });
}
